import { isEmpty, isNil } from 'lodash';
import { ILaborMetric } from 'utils/jitMetricUtils';
import { IActivityReportRowInfo } from '../hooks/ActivityReportData';
import { IProjectActivityTableRowInfo } from '../project-tables/hooks/useProjectActivity';
import ITimeRange from 'types/TimeRange';
import { DateTime } from 'luxon';
import { getCostHistoryForRange } from 'utils/equipmentUtils';
import { Equipment } from '__generated__/graphql';
import { ActivityReportType } from '../ActivityReportFilter/ActivityReportFilter';
import { remainingDataItemId } from 'utils/constants/utilConstants';

const metricToRowInfo = (
  metricTotal: ILaborMetric,
  equipment: Equipment | null,
  timeRange: ITimeRange<DateTime> | null
) => {
  let equipmentRate = 0;
  if (equipment !== null && timeRange !== null) {
    equipmentRate = getCostHistoryForRange(equipment.costHistory ?? [], timeRange)?.operatorCostRate ?? 0;
  }

  return {
    regularHours: metricTotal.regularSeconds + metricTotal.paidTimeOffSeconds,
    regularHoursDec: metricTotal.regularSeconds + metricTotal.paidTimeOffSeconds,
    overtimeHours: metricTotal.overtimeSeconds,
    overtimeHoursDec: metricTotal.overtimeSeconds,
    doubleTimeHours: metricTotal.doubleTimeSeconds,
    doubleTimeHoursDec: metricTotal.doubleTimeSeconds,
    totalHours: metricTotal.totalSeconds,
    totalHoursDec: metricTotal.totalSeconds,
    regularCost: metricTotal.regularCost + metricTotal.timeOffCost,
    overtimeCost: metricTotal.overtimeCost,
    doubletimeCost: metricTotal.doubleTimeCost,
    laborBurden: metricTotal.laborBurden,
    totalCost: metricTotal.totalCost,
    equipmentRate,
    equipmentCost: metricTotal?.equipmentCost ?? 0,
    equipmentTotalCost: (metricTotal?.equipmentCost ?? 0) + metricTotal.totalCost,
    addOnTime: metricTotal.addOnTime ?? 0,
  };
};

const rowInfoToMetric = (rowInfo: IActivityReportRowInfo): ILaborMetric => {
  return {
    regularSeconds: rowInfo.regularHours,
    overtimeSeconds: rowInfo.overtimeHours,
    doubleTimeSeconds: rowInfo.doubleTimeHours,
    totalSeconds: rowInfo.totalHours,
    regularCost: rowInfo.regularCost,
    overtimeCost: rowInfo.overtimeCost,
    doubleTimeCost: rowInfo.doubletimeCost,
    laborBurden: rowInfo.laborBurden,
    totalCost: rowInfo.totalCost,
    paidTimeOffSeconds: 0,
    timeOffSeconds: 0,
    timeOffCost: 0,
    equipmentCost: rowInfo.equipmentCost,
    addOnTime: rowInfo.addOnTime,
  };
};

export const activityReportUtils = {
  metricToRowInfo,
  rowInfoToMetric,
};

export function flattenProjects(data: IProjectActivityTableRowInfo[]): IProjectActivityTableRowInfo[] {
  return data.flatMap((item) => {
    if (item.subprojectData && !isEmpty(item.subprojectData)) {
      return [item, ...flattenProjects(item.subprojectData)];
    } else if (item.detailRows && !isEmpty(item.detailRows)) {
      return [item, ...item.detailRows];
    } else {
      return [item];
    }
  });
}

export function flattenForSubProjectExport(data: IProjectActivityTableRowInfo[]): IProjectActivityTableRowInfo[] {
  return data.flatMap((item) => {
    if (item.subprojectData && !isEmpty(item.subprojectData)) {
      return [...flattenForSubProjectsForProject(item, item.subprojectData)];
    } else {
      return [item];
    }
  });
}

function flattenForSubProjectsForProject(
  parentProject: IProjectActivityTableRowInfo,
  subProjectData: IProjectActivityTableRowInfo[]
): IProjectActivityTableRowInfo[] {
  return subProjectData.flatMap((item) => {
    let project = item.project;

    if (!isNil(project)) {
      project = {
        ...project,
        ancestors: [parentProject.project!],
      };
    }

    item.project = project;

    if (item.subprojectData && !isEmpty(item.subprojectData)) {
      return [...flattenForSubProjectsForProject(item, item.subprojectData)];
    }

    // add parent project to detail rows
    if (item.detailRows && !isEmpty(item.detailRows)) {
      item.detailRows = item.detailRows?.map((row) => {
        return {
          ...row,
          project,
        };
      });
    }

    return [item];
  });
}

export function shouldIncludeMetrics(metrics: ILaborMetric, includeAddOns: boolean) {
  return metrics.totalSeconds > 0 || (includeAddOns && (metrics.addOnTime ?? 0) > 0);
}

export function shouldIncludeActivityReportRow(metrics: IActivityReportRowInfo, includeAddOns: boolean) {
  return metrics.totalHours > 0 || (includeAddOns && (metrics.addOnTime ?? 0) > 0);
}

export function shouldIncludeAddOns(filterType: ActivityReportType, filterId: string) {
  return filterType === ActivityReportType.BY_EQUIPMENT && filterId !== remainingDataItemId;
}

export function getActivityReportSortValue(
  row: IActivityReportRowInfo,
  sortColumn: string,
  hasMultiEquipmentTracking: boolean
) {
  switch (sortColumn) {
    case 'regularHours':
    case 'regularHoursDec':
      return row.regularHours;
    case 'overtimeHours':
    case 'overtimeHoursDec':
      return row.overtimeHours;
    case 'doubleTimeHours':
    case 'doubleTimeHoursDec':
      return row.doubleTimeHours;
    case 'laborHours':
    case 'laborHoursDec':
      return row.totalHours;
    case 'addOnHours':
    case 'addOnHoursDec':
      return row.addOnTime;
    case 'totalHours':
    case 'totalHoursDec':
      return hasMultiEquipmentTracking ? row.totalHours + row.addOnTime : row.totalHours;
    case 'regularCost':
      return row.regularCost;
    case 'overtimeCost':
      return row.overtimeCost;
    case 'doubletimeCost':
      return row.doubletimeCost;
    case 'totalCost':
      return row.totalCost;
    case 'laborBurden':
      return row.laborBurden;
    case 'equipmentRate':
      return row.equipmentRate;
    case 'equipmentCost':
      return row.equipmentCost;
    case 'equipmentTotalCost':
      return row.equipmentTotalCost;
    default:
      throw new Error(`Invalid sort column: ${sortColumn}`);
  }
}
